import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';
import intervalToDuration from 'date-fns/intervalToDuration';
import { useInterval } from 'react-use';
import differenceInDays from 'date-fns/differenceInDays';

import Unit from './Unit';
import { Container } from './TimeCountdown.styled';

const TimeCountdown = ({ maxDays, time }) => {
  const { t } = useTranslation();
  const [shouldCountDown, setShouldCountDown] = useState(true);
  const [isInFocus, setIsInFocus] = useState(true);
  const [shouldAnimate, setShouldAnimate] = useState(true);

  useEffect(() => {
    let onTimeEnd;

    if (differenceInDays(time, Date.now()) <= 1) {
      onTimeEnd = setTimeout(() => {
        setShouldCountDown(false);
        window.location.reload();
      }, time - Date.now());
    }

    return () => {
      if (onTimeEnd) {
        clearTimeout(onTimeEnd);
      }
    };
  }, [time]);

  const getRemainingTime = useCallback(
    () =>
      intervalToDuration({
        start: Date.now(),
        end: time,
      }),
    [time],
  );

  const [remainingTime, setRemainingTime] = useState(getRemainingTime());

  useInterval(
    () => {
      setRemainingTime(getRemainingTime());
    },
    shouldCountDown && isInFocus ? 1000 : null,
  );

  useEffect(() => {
    const onVisibilityChange = () => {
      const isFocused = document.visibilityState === 'visible';

      if (isFocused && shouldCountDown) {
        setRemainingTime(getRemainingTime());
        setTimeout(() => {
          setShouldAnimate(true);
        }, 10);
      } else {
        setShouldAnimate(false);
      }
      setIsInFocus(isFocused);
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  return (
    <Container>
      <Unit
        value={remainingTime.days}
        max={maxDays || 1}
        label={t('timeCountdown.days', 'Tage')}
        shouldAnimate={shouldAnimate}
      />
      <Unit
        value={remainingTime.hours}
        max={24}
        label={t('timeCountdown.hours', 'Stunden')}
        shouldAnimate={shouldAnimate}
      />
      <Unit
        value={remainingTime.minutes}
        max={60}
        label={t('timeCountdown.minutes', 'Minuten')}
        shouldAnimate={shouldAnimate}
      />
      <Unit
        value={remainingTime.seconds}
        max={60}
        label={t('timeCountdown.seconds', 'Sekunden')}
        shouldAnimate={shouldAnimate}
      />
    </Container>
  );
};

TimeCountdown.propTypes = {
  maxDays: PropTypes.number.isRequired,
  time: PropTypes.number.isRequired,
};

export default TimeCountdown;
